/**
 * DeliveryHistoryDetailPage
 * 배달 상세 내역 조회 페이지
 *
 * @description
 * - 협력사/라이더 선택 후 배달 상세 내역 조회
 * - 라이더 미선택 시 라이더별 집계 뷰 표시
 * - 라이더 선택 시 상세 배달 내역 뷰 표시
 * - 컬럼별 정렬 기능
 * - 엑셀 다운로드 기능
 */
import React, { useState, useMemo, useCallback } from 'react';
import Loading from '@/shared/components/feedback/Loading';
import DateRangePicker from '@/shared/components/forms/DateRangePicker';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { useDeliveryHistoryDetail } from '../hooks/useDeliveryHistoryDetail';
import { DeliveryHistoryDetailRow, RiderSummary } from '../types/deliveryHistoryDetail.types';
import '@/shared/styles/search-form.css';
import '../styles/DeliveryHistoryDetailPage.css';

// ========================================
// 타입 정의
// ========================================
type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
    columnKey: string | null;
    direction: SortDirection;
}

// ============================================================
// 상수 정의
// ============================================================

/** 집계 뷰 컬럼 정의 */
const SUMMARY_COLUMNS = [
    { key: 'index', label: '순번', sortable: false },
    { key: 'riderId', label: '라이더 ID', sortable: true },
    { key: 'riderName', label: '라이더 이름', sortable: true },
    { key: 'deliveryCount', label: '배달 건수', sortable: true },
    { key: 'totalDeliveryAmt', label: '배달료', sortable: true },
] as const;

/** 상세 뷰 컬럼 정의 */
const DETAIL_COLUMNS = [
    { key: 'index', label: '순번' },
    { key: 'deliveryDate', label: '배달일자' },
    { key: 'deliveryStatus', label: '배달상태' },
    { key: 'deliveryMethod', label: '배달방법' },
    { key: 'distance', label: '거리(m)' },
    { key: 'goodsAmt', label: '상품가격' },
    { key: 'baseAmt', label: '기본단가' },
    { key: 'peakTimeAmt', label: '피크할증' },
    { key: 'weatherAmt', label: '기상할증' },
    { key: 'locationAmt', label: '지역할증' },
    { key: 'addAmt', label: '추가할증' },
    { key: 'deliveryAmt', label: '배달료' },
    { key: 'riderFaultYn', label: '라이더귀책' },
    { key: 'shopName', label: '가게명' },
    { key: 'orderTime', label: '주문시간' },
    { key: 'assingTime', label: '배차완료시간' },
    { key: 'shopArriTime', label: '가게도착시간' },
    { key: 'pickupTime', label: '픽업완료시간' },
    { key: 'deliveryTime', label: '배달완료시간' },
    { key: 'pickupAddr', label: '픽업주소' },
    { key: 'deliveryAddr', label: '배달주소' },
    { key: 'addAmtReason', label: '추가할증사유' },
] as const;

// ============================================================
// 헬퍼 함수
// ============================================================

/**
 * 범용 정렬 비교 함수
 */
const compareValues = <T,>(a: T, b: T, key: keyof T, direction: SortDirection): number => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction === 'asc' ? 1 : -1;
    if (bVal == null) return direction === 'asc' ? -1 : 1;

    // 숫자 비교
    if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    // 문자역 비교
    const aStr = String(aVal);
    const bStr = String(bVal);
    return direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
};

// ============================================================
// 컴포넌트
// ============================================================
const DeliveryHistoryDetailPage: React.FC = () => {
    const {
        filters,
        results,
        riderSummaries,
        summaryInfo,
        isSummaryView,
        loading,
        error,
        handleFilterChange,
        handleSubmit,
        contextInfo,
        suppliers,
        suppliersLoading,
        riders,
        ridersLoading,
        isSupplierUser,
        excelDownloading,
        handleExcelDownload,
    } = useDeliveryHistoryDetail();

    // 헤더 정보
    const {
        suppId: headerSuppId,
        suppName: headerSuppName,
        riderId: headerRiderId,
        riderName: headerRiderName,
    } = contextInfo;

    // 정렬 상태
    const [summarySortConfig, setSummarySortConfig] = useState<SortConfig>({
        columnKey: null,
        direction: null,
    });
    const [detailSortConfig, setDetailSortConfig] = useState<SortConfig>({
        columnKey: null,
        direction: null,
    });

    /**
     * 정렬 토글
     */
    const createSortHandler = useCallback(
        (setConfig: React.Dispatch<React.SetStateAction<SortConfig>>) => (columnKey: string) => {
            if (columnKey === 'index') return;

            setConfig((prev) => {
                if (prev.columnKey === columnKey) {
                    if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
                    if (prev.direction === 'desc') return { columnKey: null, direction: null };
                }
                return { columnKey, direction: 'asc' };
            });
        },
        []
    );

    const handleSummarySort = useMemo(() => createSortHandler(setSummarySortConfig), [createSortHandler]);
    const handleDetailSort = useMemo(() => createSortHandler(setDetailSortConfig), [createSortHandler]);

    /**
     * 정렬된 집계 데이터
     */
    const sortedSummaries = useMemo(() => {
        if (!riderSummaries || !summarySortConfig.columnKey || !summarySortConfig.direction) {
            return riderSummaries || [];
        }

        return [...riderSummaries].sort((a, b) =>
            compareValues(a, b, summarySortConfig.columnKey as keyof RiderSummary, summarySortConfig.direction)
        );
    }, [riderSummaries, summarySortConfig]);

    /**
     * 정렬된 상세 데이터
     */
    const sortedResults = useMemo(() => {
        if (!results || !detailSortConfig.columnKey || !detailSortConfig.direction) {
            return results || [];
        }

        return [...results].sort((a, b) =>
            compareValues(
                a,
                b,
                detailSortConfig.columnKey as keyof DeliveryHistoryDetailRow,
                detailSortConfig.direction
            )
        );
    }, [results, detailSortConfig]);

    /**
     * 상세 뷰 총 배달료 계산
     */
    const totalDeliveryAmt = useMemo(() => results.reduce((sum, row) => sum + (row.deliveryAmt || 0), 0), [results]);

    /**
     * 협력사 옵션 (SelectOption 형식)
     */
    const supplierSelectOptions: SelectOption[] = useMemo(() => {
        const options = [...suppliers];
        if (filters.suppId && !options.some((s) => s.suppId === filters.suppId)) {
            options.unshift({
                suppId: filters.suppId,
                suppName: filters.suppName || filters.suppId,
            });
        }
        return options.map((s) => ({ value: s.suppId, label: s.suppName }));
    }, [suppliers, filters.suppId, filters.suppName]);

    /**
     * 정렬 아이콘 렌더링
     */
    const renderSortIcon = (columnKey: string, sortConfig: SortConfig, isIndex = false) => {
        if (isIndex) return null; // 순번 컬럼은 정렬 아이콘 없음

        const isSorted = sortConfig.columnKey === columnKey;
        const direction = isSorted ? sortConfig.direction : null;

        return (
            <span className="sort-icon">
                {direction === 'asc' && <i className="fas fa-sort-up"></i>}
                {direction === 'desc' && <i className="fas fa-sort-down"></i>}
                {!direction && <i className="fas fa-sort" style={{ opacity: 0.3 }}></i>}
            </span>
        );
    };

    /**
     * 협력사 선택 핸들러
     */
    const handleSupplierChange = useCallback(
        (selectedId: string) => {
            const selectedSupplier = suppliers.find((s) => s.suppId === selectedId);
            handleFilterChange('suppId', selectedId);
            handleFilterChange('suppName', selectedSupplier?.suppName ?? '');
            handleFilterChange('riderId', '');
            handleFilterChange('riderName', '');
        },
        [suppliers, handleFilterChange]
    );

    /**
     * 라이더 선택 핸들러
     */
    const handleRiderChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedId = e.target.value;
            const selectedRider = riders.find((r) => r.riderId === selectedId);
            handleFilterChange('riderId', selectedId);
            handleFilterChange('riderName', selectedRider?.riderName ?? '');
        },
        [riders, handleFilterChange]
    );

    return (
        <div className="delivery-history-detail-page">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">배달 상세 내역</h1>
            </div>

            {/* 검색 폼 */}
            <div className="card search-card">
                <div className="card-body">
                    <form className={`search-form ${isSupplierUser ? 'supplier-view' : ''}`} onSubmit={handleSubmit}>
                        {/* 날짜 범위 선택 */}
                        <div className="form-group date-range-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    조회 기간
                                </label>
                                <DateRangePicker
                                    startDate={filters.startDate}
                                    endDate={filters.endDate}
                                    onStartDateChange={(value) => handleFilterChange('startDate', value)}
                                    onEndDateChange={(value) => handleFilterChange('endDate', value)}
                                    startLabel="시작일"
                                    endLabel="종료일"
                                />
                            </div>
                        </div>

                        {/* 협력사 선택 (본사 사용자만) */}
                        {!isSupplierUser && (
                            <div className="form-group">
                                <div className="form-field">
                                    <label className="form-label">
                                        <i className="fas fa-building" />
                                        협력사
                                    </label>
                                    <SearchableSelect
                                        options={supplierSelectOptions}
                                        value={filters.suppId}
                                        onChange={handleSupplierChange}
                                        placeholder="협력사 검색..."
                                        disabled={suppliersLoading}
                                        showAllOption={false}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 라이더 선택 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-motorcycle" />
                                    라이더
                                </label>
                                <div className="rider-select-wrapper">
                                    <select
                                        className="rider-select"
                                        value={filters.riderId}
                                        onChange={handleRiderChange}
                                        disabled={!filters.suppId || ridersLoading || riders.length === 0}
                                    >
                                        <option value="">전체</option>
                                        {riders.map((r) => (
                                            <option key={r.riderId} value={r.riderId}>
                                                {r.riderName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 버튼 그룹 */}
                        <div className="form-group button-group">
                            <button
                                type="button"
                                className="btn btn-success excel-btn"
                                onClick={handleExcelDownload}
                                disabled={loading || excelDownloading || !filters.startDate || !filters.endDate}
                            >
                                {excelDownloading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        다운로드 중...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-file-excel"></i>
                                        엑셀
                                    </>
                                )}
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading || !filters.suppId}>
                                <i className="fas fa-search" />
                                검색
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* 결과 테이블 */}
            <div className="card">
                <div className="card-header delivery-history-detail-header">
                    <div>
                        <h3 className="font-semibold">상세 내역</h3>
                        {(headerSuppName || headerSuppId || headerRiderId || headerRiderName) && (
                            <div className="detail-identifier-caption">
                                {(headerSuppName || headerSuppId) && (
                                    <span className="detail-identifier">
                                        {headerSuppName
                                            ? `${headerSuppName}${headerSuppId ? ` (${headerSuppId})` : ''}`
                                            : headerSuppId}
                                    </span>
                                )}
                                {headerRiderId && (
                                    <>
                                        <span className="detail-identifier-separator">·</span>
                                        <span className="detail-identifier">
                                            {headerRiderName ? `${headerRiderName} (${headerRiderId})` : headerRiderId}
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="card-body">
                    {/* 결과 요약 */}
                    {isSummaryView && summaryInfo.totalDeliveryCount !== undefined && (
                        <div className="result-count">
                            <i className="fas fa-list"></i>총 배달 건수:{' '}
                            <strong>{summaryInfo.totalDeliveryCount.toLocaleString()}건</strong>
                            <span className="result-count-separator">|</span>총 배달료:{' '}
                            <strong>{summaryInfo.totalDeliveryAmt || '0'}원</strong>
                        </div>
                    )}
                    {!isSummaryView && results.length > 0 && (
                        <div className="result-count">
                            <i className="fas fa-list"></i>총 배달 건수:{' '}
                            <strong>{results.length.toLocaleString()}건</strong>
                            <span className="result-count-separator">|</span>총 배달료:{' '}
                            <strong>{totalDeliveryAmt.toLocaleString()}원</strong>
                        </div>
                    )}

                    {/* 컨텐츠 영역 */}
                    {loading ? (
                        <Loading />
                    ) : error ? (
                        <div className="empty-state">
                            <h3>오류가 발생했습니다</h3>
                            <p>{error}</p>
                        </div>
                    ) : isSummaryView ? (
                        /* 집계 뷰 */
                        <div className="delivery-history-detail-table-responsive has-summary-header">
                            <table className="delivery-history-detail-table summary-table sortable-table">
                                <thead>
                                    <tr>
                                        {SUMMARY_COLUMNS.map((col) => (
                                            <th
                                                key={col.key}
                                                className={`${col.sortable ? 'sortable-header' : ''} ${summarySortConfig.columnKey === col.key ? 'sorted' : ''}`}
                                                onClick={() => col.sortable && handleSummarySort(col.key)}
                                            >
                                                {col.label}
                                                {renderSortIcon(col.key, summarySortConfig, !col.sortable)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedSummaries.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="empty-state">
                                                조회된 라이더 집계 내역이 없습니다.
                                            </td>
                                        </tr>
                                    ) : (
                                        sortedSummaries.map((summary, index) => (
                                            <tr key={summary.riderId}>
                                                <td>{index + 1}</td>
                                                <td>{summary.riderId}</td>
                                                <td>{summary.riderName}</td>
                                                <td>{summary.deliveryCount.toLocaleString()}</td>
                                                <td>{summary.totalDeliveryAmt}원</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        /* 상세 뷰 */
                        <div className="delivery-history-detail-table-responsive">
                            <table className="delivery-history-detail-table detail-table sortable-table">
                                <thead>
                                    <tr>
                                        {DETAIL_COLUMNS.map((col) => (
                                            <th
                                                key={col.key}
                                                className={`${col.key !== 'index' ? 'sortable-header' : ''} ${detailSortConfig.columnKey === col.key ? 'sorted' : ''}`}
                                                onClick={() => handleDetailSort(col.key)}
                                            >
                                                {col.label}
                                                {renderSortIcon(col.key, detailSortConfig, col.key === 'index')}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedResults.length === 0 ? (
                                        <tr>
                                            <td colSpan={22} className="empty-state">
                                                조회된 상세 내역이 없습니다.
                                            </td>
                                        </tr>
                                    ) : (
                                        sortedResults.map((row, index) => (
                                            <tr key={`${row.deliveryNo}-${row.riderId}`}>
                                                <td>{index + 1}</td>
                                                <td>{row.deliveryDate}</td>
                                                <td>{row.deliveryStatus}</td>
                                                <td>{row.deliveryMethod}</td>
                                                <td>{row.distance.toLocaleString()}</td>
                                                <td>{row.goodsAmt.toLocaleString()}</td>
                                                <td>{row.baseAmt.toLocaleString()}</td>
                                                <td>{row.peakTimeAmt.toLocaleString()}</td>
                                                <td>{row.weatherAmt.toLocaleString()}</td>
                                                <td>{row.locationAmt.toLocaleString()}</td>
                                                <td>{row.addAmt.toLocaleString()}</td>
                                                <td>{row.deliveryAmt.toLocaleString()}</td>
                                                <td>{row.riderFaultYn}</td>
                                                <td>{row.shopName}</td>
                                                <td>{row.orderTime}</td>
                                                <td>{row.assingTime}</td>
                                                <td>{row.shopArriTime}</td>
                                                <td>{row.pickupTime}</td>
                                                <td>{row.deliveryTime}</td>
                                                <td>{row.pickupAddr}</td>
                                                <td>{row.deliveryAddr}</td>
                                                <td>{row.addAmtReason}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeliveryHistoryDetailPage;
